import { ChangeEvent, useState } from "react";

import { DataGrid, GridColDef } from "@mui/x-data-grid";

import {
  alpha,
  CardMedia,
  colors,
  Grid,
  Icon,
  Typography,
} from "@mui/material";

import FullScreenDialog from "components/FullScreenDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { formatDateEndOfDay } from "helpers/formatDate";

import { IResultadoCorrida } from "interfaces/resultadoCorrida";

// imagens
import pdf_transparent from "assets/images/icons/pdf_transparent.png";
import logoTnv from "assets/images/favicon.png";
import imgWiclax from "assets/images/wiclax.webp";

import ModalFullWiclax from "./ModalFullWiclax";
import ModalFullFilePDF from "./ModalFullFilePDF";
import FormField from "layouts/pages/account/components/FormField";
import { addOrEditResultados } from "services/resultadoCorrida";
import theme from "assets/theme";

interface Props {
  isLoading: boolean;
  isUpdate: () => void;
  data: IResultadoCorrida[];
  viewMode: "pendentes" | "publicados";
}

export default function TableResultadosEventos({
  data,
  isLoading,
  isUpdate,
  viewMode,
}: Props) {
  const mode = theme.palette.mode;
  const primaryMain = theme.palette.primary.main;
  const grey500 = theme.palette.grey[500];
  const [openModalWiclax, setOpenModalWiclax] = useState<boolean>(false);
  const [openModalFilePDF, setOpenModalFilePDF] = useState<boolean>(false);
  const [selectedEvento, setSelectedEvento] =
    useState<IResultadoCorrida | null>(null);
  const [urlPhotoEvento, setUrlPhotoEvento] = useState<string>("");

  const borderSoft =
    mode === "dark" ? alpha("#90caf9", 0.35) : alpha("#1976d2", 0.22);

  const isPublicado = viewMode === "publicados";
  const actionLabel = isPublicado ? "Visualizar" : "Adicionar resultado";

  // Columns da tabela
  const columns: GridColDef<IResultadoCorrida>[] = [
    {
      field: "imagem",
      headerName: "",
      width: 96,
      sortable: false,
      renderCell({ row }) {
        const src = row.urlImageDestaque || row.urlBanner || logoTnv;
        return (
          <MDBox
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={src}
              alt={row.titulo || "Imagem do evento"}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = logoTnv;
              }}
              style={{
                width: 72,
                height: 48,
                objectFit: "cover",
                objectPosition: "center",
                borderRadius: 4,
                border: `1px solid ${borderSoft}`,
                backgroundColor: alpha(grey500, 0.06),
                display: "block",
                flexShrink: 0,
              }}
            />
          </MDBox>
        );
      },
    },
    {
      field: "titulo",
      headerName: "Evento",
      flex: 1,
      minWidth: 280,
    },
    {
      field: "dataEvento",
      headerName: "Dt. Evento",
      width: 150,
      renderCell(params) {
        return (
          <Typography variant="body2">
            {formatDateEndOfDay(params.row.dataEvento, "dd/MM/yyyy")}
          </Typography>
        );
      },
    },
    {
      field: "cidade",
      headerName: "Cidade",
      width: 200,
      renderCell(params) {
        return (
          <Typography variant="body2">
            {params.row.endereco[0].cidade}/{params.row.endereco[0].uf}
          </Typography>
        );
      },
    },
    {
      field: "action",
      headerName: "Ação",
      headerAlign: "center",
      align: "center",
      width: 350,
      renderCell(params) {
        return (
          <MDButton
            variant="outlined"
            color={isPublicado ? "success" : "info"}
            onClick={() => handleSelectEvento(params.row)}
          >
            {actionLabel}
          </MDButton>
        );
      },
    },
  ];

  // Selecionar o evento
  const handleSelectEvento = (evento: IResultadoCorrida) => {
    setSelectedEvento(evento);
    setUrlPhotoEvento(evento?.resultado?.urllink || "");
  };

  const handleUpdate = () => {
    setOpenModalWiclax(false);
    setOpenModalFilePDF(false);
    setSelectedEvento(null);
    isUpdate();
  };

  const handleAddOrUpdateUrlPhoto = async () => {
    addOrEditResultados({
      urllink: urlPhotoEvento,
      eventoId: selectedEvento?.id,
    }).then(() => {
      handleUpdate();
    });
  };

  return (
    <MDBox sx={{ height: "100%", width: "100%" }}>
      <DataGrid
        sx={{
          border: `1px solid ${borderSoft}`,
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(primaryMain, 0.04),
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(grey500, 0.08),
            fontWeight: 700,
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
        rowHeight={56}
        columnHeaderHeight={44}
        getRowId={(row) => row.slug}
        columns={columns}
        rows={data}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        localeText={{
          noRowsLabel: isPublicado
            ? "Nenhum evento com resultados publicados"
            : "Nenhum evento com resultados pendentes",
        }}
        autoHeight
        disableRowSelectionOnClick
      />

      <FullScreenDialog
        open={selectedEvento ? true : false}
        title={isPublicado ? "Visualizar resultado" : "Adicionar Resultado"}
        handleClose={() => setSelectedEvento(null)}
      >
        <MDBox
          mx={{ md: 8, xs: 2 }}
          my={2}
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          {/* HEADER */}
          <Typography variant="body1" sx={{ mb: 1 }}>
            Adicionar o resultado da corrida
          </Typography>

          <MDBox
            display="flex"
            flexDirection={{ md: "row", xs: "column" }}
            gap={2}
            sx={{
              border: `1px dashed ${colors.grey[400]}`,
              borderRadius: "5px",
              p: 2,
              bgcolor: colors.grey[50],
            }}
          >
            <img
              src={
                selectedEvento?.urlImageDestaque ||
                selectedEvento?.urlBanner ||
                logoTnv
              }
              alt={selectedEvento?.titulo}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = logoTnv;
              }}
              style={{
                width: 120,
                height: 80,
                objectFit: "cover",
                borderRadius: 4,
                border: `1px solid ${colors.grey[300]}`,
                flexShrink: 0,
              }}
            />

            <MDBox sx={{ ml: 2, display: "flex", flexDirection: "column" }}>
              <Typography variant="h5">{selectedEvento?.titulo}</Typography>
              <Typography variant="subtitle2">
                <strong>Data evento:</strong>{" "}
                {formatDateEndOfDay(
                  selectedEvento?.dataEvento,
                  "EEEE, dd 'de' MMMM 'de' yyyy"
                )}{" "}
                - <strong>Hora largada:</strong> {selectedEvento?.horaLargada}
              </Typography>

              <Typography variant="subtitle2">
                {selectedEvento?.endereco[0].cidade}/
                {selectedEvento?.endereco[0].uf.toUpperCase()}
              </Typography>
            </MDBox>
          </MDBox>

          {/* BODY */}
          <MDBox display="flex" justifyContent="center" sx={{ mt: 2 }}>
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap={2}
              p={2}
              width={{ md: "60%", xs: "95%" }}
            >
              <Typography
                variant="body1"
                p={2}
                width="100%"
                color={colors.grey[700]}
              >
                Como deseja enviar seu resultado da corrida para o site, você
                pode escolhe as opções abaixo
              </Typography>

              {/* BUTTON SHOW MODAL WICLAX */}
              <MDBox
                position="relative"
                component="button"
                onClick={() => setOpenModalWiclax((prev) => !prev)}
                variant="gradient"
                sx={{
                  cursor: "pointer",
                  p: 2,
                  width: "100%",
                  height: "auto",
                  backgroundColor: colors.grey[50],
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: "5px",
                  "&:hover": {
                    border: `2px dashed ${colors.grey[500]}`,
                  },
                }}
              >
                {selectedEvento?.resultado?.filenameWiclax && (
                  <MDBox position="absolute" right={10} top={10}>
                    <Icon color="success" fontSize="medium">
                      verified
                    </Icon>
                  </MDBox>
                )}
                <CardMedia
                  component="img"
                  src={imgWiclax}
                  height={30}
                  alt="wiclax"
                  style={{ width: "auto" }}
                />
                <Typography variant="subtitle2">
                  Conectar seu wiclax para enviar o resultadoa automaticamente
                  pelo ftp
                </Typography>
              </MDBox>
              {/* BUTTOM SHOW MODAL ADD FILES PDF */}
              <MDBox
                position="relative"
                component="button"
                variant="gradient"
                onClick={() => setOpenModalFilePDF((prev) => !prev)}
                sx={{
                  cursor: "pointer",
                  p: 2,
                  width: "100%",
                  height: "auto",
                  backgroundColor: colors.grey[50],
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: "5px",
                  "&:hover": {
                    border: `2px dashed ${colors.grey[500]}`,
                  },
                }}
              >
                {selectedEvento?.resultado?.filenamePdf.length > 0 && (
                  <MDBox position="absolute" right={10} top={10}>
                    <Icon color="success" fontSize="medium">
                      verified
                    </Icon>
                  </MDBox>
                )}
                <CardMedia
                  component="img"
                  src={pdf_transparent}
                  alt="pdf"
                  height={30}
                  style={{ width: "auto" }}
                />
                <Typography variant="subtitle2">
                  Enviar os resultados manualmente no formato PDF
                </Typography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* FORM URL PHOTO EVENTO  */}
          <MDBox
            sx={{
              p: 2,
              my: 1,
              backgroundColor: colors.grey[50],
              borderRadius: "6px",
              border: `1px solid ${colors.grey[400]}`,
            }}
          >
            <MDBox
              sx={{
                mb: 4,
                borderLeft: `4px solid ${colors.orange[400]}`,
                pl: 1,
              }}
            >
              <Typography variant="body2">
                Definir o link das fotos do evento, cole o link da url no campo
                abaixo
              </Typography>
            </MDBox>

            {/* FORM URL PHOTO EVENTO */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={10}>
                <FormField
                  type="text"
                  label="Link das fotos do evento"
                  name="urlImageDestaque"
                  placeholder="https://..."
                  value={urlPhotoEvento}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setUrlPhotoEvento(event.target.value);
                  }}
                  sx={{ background: "white" }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={2}
                container
                justifyContent="center"
                alignItems="center"
              >
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  onClick={handleAddOrUpdateUrlPhoto}
                >
                  Salvar
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </FullScreenDialog>

      {/* MODAL FULL WICLAX */}
      {selectedEvento && (
        <ModalFullWiclax
          open={openModalWiclax}
          onClose={() => setOpenModalWiclax(!openModalWiclax)}
          selectedEvento={selectedEvento}
          isUpdate={handleUpdate}
        />
      )}

      {/* MODAL FULL FILE PDF */}
      {selectedEvento && (
        <ModalFullFilePDF
          open={openModalFilePDF}
          onClose={() => setOpenModalFilePDF(!openModalFilePDF)}
          selectedEvento={selectedEvento}
          isUpdate={handleUpdate}
        />
      )}
    </MDBox>
  );
}
